import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

interface ConfirmDialogProps {
    open: boolean;
    title: ReactNode;
    message: ReactNode;
    /**
     * Anything that must sit OUTSIDE the message paragraph — an Alert spelling
     * out what else the action takes with it, for instance.
     */
    details?: ReactNode;
    /** Defaults to the shared "Delete" label. */
    confirmLabel?: ReactNode;
    /** Defaults to the shared "Cancel" label. */
    cancelLabel?: ReactNode;
    confirmColor?: 'error' | 'primary';
    onConfirm: () => void;
    onCancel: () => void;
}

export default function ConfirmDialog({
    open,
    title,
    message,
    details,
    confirmLabel,
    cancelLabel,
    confirmColor = 'primary',
    onConfirm,
    onCancel
}: ConfirmDialogProps) {
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
                {details}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onCancel}>
                    {cancelLabel ?? <FormattedMessage id="common.cancel" />}
                </Button>
                <Button
                    color={confirmColor}
                    variant="contained"
                    onClick={onConfirm}
                >
                    {confirmLabel ?? <FormattedMessage id="common.delete" />}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
